import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Grid,
  CircularProgress,
  Alert,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Skeleton
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Receipt as ReceiptIcon,
  Download as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  CalendarMonth as CalendarIcon,
  Payment as PaymentIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as HourglassIcon,
  Warning as WarningIcon,
  Cancel as CancelIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  Send as SendIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import ownerCommissionService from '../../services/ownerCommissionService';
import OverduePayinsSection from './OverduePayinsSection';

// ─────────────────────────────────────────────
// Config statuts (identique à OwnerInvoicesPage)
// ─────────────────────────────────────────────
const STATUS_CONFIG = {
  draft:             { color: 'default',   icon: <ScheduleIcon />,     muiColor: '#9e9e9e', bg: '#f5f5f5', labelKey: 'draft' },
  pending:           { color: 'warning',   icon: <HourglassIcon />,    muiColor: '#f57c00', bg: '#fff8e1', labelKey: 'pending' },
  link_sent:         { color: 'info',      icon: <SendIcon />,         muiColor: '#0288d1', bg: '#e1f5fe', labelKey: 'link_sent' },
  payment_initiated: { color: 'secondary', icon: <PaymentIcon />,      muiColor: '#7b1fa2', bg: '#f3e5f5', labelKey: 'payment_initiated' },
  paid:              { color: 'success',   icon: <CheckCircleIcon />,  muiColor: '#2e7d32', bg: '#e8f5e9', labelKey: 'paid' },
  overdue:           { color: 'error',     icon: <WarningIcon />,      muiColor: '#c62828', bg: '#ffebee', labelKey: 'overdue' },
  cancelled:         { color: 'default',   icon: <CancelIcon />,       muiColor: '#757575', bg: '#eeeeee', labelKey: 'cancelled' },
  failed:            { color: 'error',     icon: <ErrorIcon />,        muiColor: '#b71c1c', bg: '#ffcdd2', labelKey: 'failed' },
};

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────
const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatAmount = (amount, currency = 'EUR') => {
  if (amount == null) return '—';
  try {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency }).format(amount);
  } catch {
    return `${amount} ${currency}`;
  }
};

// ─────────────────────────────────────────────
// Ligne info réutilisable
// ─────────────────────────────────────────────
const InfoRow = ({ label, value, valueColor, bold }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      py: 1,
      borderBottom: '1px solid #f0f0f0',
      '&:last-child': { borderBottom: 'none' }
    }}
  >
    <Typography variant="body2" sx={{ color: '#666' }}>{label}</Typography>
    <Typography
      variant="body2"
      sx={{ fontWeight: bold ? 700 : 500, color: valueColor || '#1a1a2e', textAlign: 'right', ml: 2 }}
    >
      {value}
    </Typography>
  </Box>
);

// ─────────────────────────────────────────────
// Page principale
// ─────────────────────────────────────────────
const OwnerInvoiceDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation('financial');

  const [invoice, setInvoice] = useState(null);
  const [payins, setPayins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadingPayins, setLoadingPayins] = useState(false);
  const [payinsPage, setPayinsPage] = useState(1);
  const [hasMorePayins, setHasMorePayins] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  // ─── Chargement de la facture ───
  useEffect(() => {
    const fetchInvoice = async () => {
      if (!id) return;
      setLoading(true);
      setError(null);
      try {
        const data = await ownerCommissionService.getInvoiceDetails(id);
        setInvoice(data);
      } catch (err) {
        console.error('Erreur chargement facture:', err);
        setError(t('invoiceDetail.error', 'Impossible de charger cette facture.'));
      } finally {
        setLoading(false);
      }
    };
    fetchInvoice();
  }, [id, t]);

  // ─── Chargement des payins (lazy, à l'ouverture de l'accordéon) ───
  const loadPayins = useCallback(async (page = 1, reset = false) => {
    if (loadingPayins || !id) return;
    setLoadingPayins(true);
    try {
      const data = await ownerCommissionService.getInvoicePayinsDetails(id, { page, limit: 10 });
      if (reset) {
        setPayins(data.payins || []);
      } else {
        setPayins(prev => [...prev, ...(data.payins || [])]);
      }
      setHasMorePayins(data.pagination?.hasMore || false);
      setPayinsPage(page);
    } catch (err) {
      console.error('Erreur chargement payins:', err);
    } finally {
      setLoadingPayins(false);
    }
  }, [id, loadingPayins]);

  const handleAccordionChange = (_, expanded) => {
    if (expanded && payins.length === 0) {
      loadPayins(1, true);
    }
  };

  // ─── Téléchargement PDF ───
  const handleDownloadPdf = async () => {
    if (downloadingPdf || !id) return;
    setDownloadingPdf(true);
    try {
      const blob = await ownerCommissionService.downloadInvoicePdf(id);
      if (!blob || blob.size === 0) throw new Error('PDF vide');
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `facture-commission-${invoice?.invoice_number || id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Erreur téléchargement PDF:', err);
      alert(t('invoices.pdfError', 'Erreur lors du téléchargement du PDF.'));
    } finally {
      setDownloadingPdf(false);
    }
  };

  // ─── Rendu chargement ───
  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 2, mb: 3 }} />
        <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 3, mb: 2 }} />
        <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 3 }} />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/owner/invoices')}>
          {t('invoiceDetail.backToList', 'Retour aux factures')}
        </Button>
      </Container>
    );
  }

  if (!invoice) return null;

  const cfg = STATUS_CONFIG[invoice.status] || STATUS_CONFIG.pending;
  const canPay = ['pending', 'link_sent', 'overdue'].includes(invoice.status);
  const isPaid = invoice.status === 'paid';

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* ── Navigation retour ── */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/owner/invoices')}
        sx={{ mb: 3, borderRadius: 2 }}
      >
        {t('invoiceDetail.backToList', 'Retour aux factures')}
      </Button>

      {/* ── En-tête de la facture ── */}
      <Paper
        elevation={2}
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          mb: 3,
          border: `1px solid ${cfg.muiColor}30`
        }}
      >
        {/* Bandeau statut */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${cfg.muiColor}20 0%, ${cfg.bg} 100%)`,
            borderBottom: `2px solid ${cfg.muiColor}40`,
            px: 3,
            py: 2.5,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <ReceiptIcon sx={{ color: cfg.muiColor, fontSize: 28 }} />
            <Box>
              <Typography variant="caption" sx={{ color: '#666', textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}>
                {t('invoices.invoiceNumber', 'Facture')}
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#1a1a2e', lineHeight: 1.2 }}>
                {invoice.invoice_number}
              </Typography>
            </Box>
          </Box>
          <Chip
            icon={React.cloneElement(cfg.icon, { fontSize: 'small' })}
            label={t(`invoices.status.${invoice.status}`, invoice.status)}
            color={cfg.color}
            sx={{ fontWeight: 700, fontSize: '0.9rem', px: 1 }}
          />
        </Box>

        {/* Montant principal */}
        <Box sx={{ px: 3, py: 3, textAlign: 'center', background: '#fafafa' }}>
          <Typography variant="caption" sx={{ color: '#888', textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}>
            {t('invoiceDetail.totalAmount', 'Montant total')}
          </Typography>
          <Typography variant="h3" sx={{ fontWeight: 900, color: cfg.muiColor, my: 0.5 }}>
            {formatAmount(invoice.total_amount, invoice.currency)}
          </Typography>
          {invoice.payins_count > 0 && (
            <Typography variant="body2" sx={{ color: '#888' }}>
              {t('invoices.payinsCount', { count: invoice.payins_count })}
            </Typography>
          )}
        </Box>
      </Paper>

      {/* ── Informations de la facture ── */}
      <Card elevation={1} sx={{ borderRadius: 3, mb: 3 }}>
        <CardContent sx={{ px: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <CalendarIcon sx={{ color: '#1976d2' }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {t('invoiceDetail.invoiceInfo', 'Informations de la facture')}
            </Typography>
          </Box>

          <InfoRow
            label={t('invoices.period', 'Période')}
            value={`${formatDate(invoice.period_start)} — ${formatDate(invoice.period_end)}`}
          />
          <InfoRow
            label={t('invoiceDetail.createdAt', 'Créée le')}
            value={formatDate(invoice.created_at)}
          />
          {invoice.due_date && (
            <InfoRow
              label={t('invoices.dueDate', 'Échéance')}
              value={formatDate(invoice.due_date)}
              valueColor={invoice.status === 'overdue' ? '#c62828' : undefined}
              bold={invoice.status === 'overdue'}
            />
          )}
          {isPaid && (
            <InfoRow
              label={t('invoices.paidDate', 'Payé le')}
              value={invoice.paid_date ? formatDate(invoice.paid_date) : '—'}
              valueColor="#2e7d32"
              bold
            />
          )}
          {isPaid && invoice.payment_method && (
            <InfoRow
              label={t('invoiceDetail.paymentMethod', 'Méthode de paiement')}
              value={invoice.payment_method}
            />
          )}
        </CardContent>
      </Card>

      {/* ── Actions ── */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        {canPay && invoice.stripe_payment_link_url && (
          <Button
            variant="contained"
            color="warning"
            size="large"
            startIcon={<PaymentIcon />}
            href={invoice.stripe_payment_link_url}
            target="_blank"
            rel="noopener noreferrer"
            endIcon={<OpenInNewIcon />}
            sx={{ borderRadius: 2, fontWeight: 700, flex: 1 }}
          >
            {t('invoices.payNow', 'Payer maintenant')}
          </Button>
        )}
        <Button
          variant="outlined"
          size="large"
          startIcon={downloadingPdf ? <CircularProgress size={18} /> : <DownloadIcon />}
          onClick={handleDownloadPdf}
          disabled={downloadingPdf}
          sx={{ borderRadius: 2, fontWeight: 600, flex: canPay ? 'unset' : 1 }}
        >
          {downloadingPdf
            ? t('invoices.downloading', '…')
            : t('invoices.downloadPdf', 'Télécharger PDF')}
        </Button>
      </Box>

      {/* ── Banner de rattrapage (factures overdue_recovery) ── */}
      {invoice.metadata?.invoice_type === 'overdue_recovery' && (
        <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2, borderRadius: 2 }}>
          {t('overdue.recoveryBanner', 'Facture de rattrapage — paiements en retard des mois précédents')}
        </Alert>
      )}

      {/* ── Détails des commandes (accordéon) ── */}
      <Card elevation={1} sx={{ borderRadius: 3 }}>
        <Accordion onChange={handleAccordionChange} sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 3, py: 0.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {t('invoiceDetail.ordersDetail', 'Détail des commandes')}
              {invoice.payins_count > 0 && (
                <Typography component="span" variant="body2" sx={{ ml: 1, color: '#888', fontWeight: 400 }}>
                  ({invoice.payins_count})
                </Typography>
              )}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 3, pt: 0, pb: 3 }}>
            {payins.length === 0 && !loadingPayins ? (
              <Typography variant="body2" sx={{ color: '#888', py: 2 }}>
                {t('invoiceDetail.noOrders', 'Aucune commande à afficher.')}
              </Typography>
            ) : invoice.metadata?.invoice_type === 'overdue_recovery' ? (
              <OverduePayinsSection payins={payins} />
            ) : (
              payins.map((payin) => (
                <Paper
                  key={payin.id}
                  elevation={0}
                  sx={{ mb: 2, p: 2.5, borderRadius: 2, border: '1px solid #e0e0e0', background: '#fafafa' }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, color: '#1a1a2e' }}>
                    {payin.reservation?.listing?.title || t('financial.listingUnknown', 'Listing inconnu')}
                  </Typography>

                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" sx={{ color: '#888' }}>
                        {t('commissionPaymentSuccess.reservationPeriod', 'Période')}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatDate(payin.reservation?.start_date)} — {formatDate(payin.reservation?.end_date)}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" sx={{ color: '#888' }}>
                        {t('commissionPaymentSuccess.cashReceived', 'Montant cash reçu')}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatAmount(payin.cash_amount_received, payin.original_currency)}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" sx={{ color: '#888' }}>
                        {t('commissionPaymentSuccess.commissionRate', 'Taux de commission')}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {payin.commission_rate_percentage ? `${payin.commission_rate_percentage}%` : `${(payin.commission_rate * 100).toFixed(2)}%`}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" sx={{ color: '#888' }}>
                        {t('commissionPaymentSuccess.commissionAmount', 'Commission')}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: cfg.muiColor }}>
                        {formatAmount(payin.commission_amount, payin.original_currency)}
                      </Typography>
                    </Grid>

                    {payin.taxes > 0 && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" sx={{ color: '#888' }}>
                          {t('commissionPaymentSuccess.taxes', 'Taxes')}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {formatAmount(payin.taxes, payin.original_currency)}
                        </Typography>
                      </Grid>
                    )}

                    {payin.original_currency && payin.original_currency !== payin.currency ? (
                      <>
                        <Grid item xs={12}>
                          <Divider sx={{ my: 0.5 }} />
                        </Grid>

                        {/* Montant commission converti */}
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" sx={{ color: '#888' }}>
                            {t('commissionPaymentSuccess.commissionConverted', 'Montant commission')}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {payin.converted_amount_with_currency || formatAmount(payin.converted_amount, payin.currency)}
                          </Typography>
                        </Grid>

                        {/* Taxes converties */}
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" sx={{ color: '#888' }}>
                            {t('commissionPaymentSuccess.taxesConverted', 'Taxes converties')}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {formatAmount(
                              Number(
                                ((payin.exchange_rate ? payin.taxes / payin.exchange_rate : payin.taxes) || 0).toFixed(2)
                              ),
                              payin.currency
                            )}
                          </Typography>
                        </Grid>

                        {/* Total converti */}
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" sx={{ color: '#888' }}>
                            {t('commissionPaymentSuccess.totalConverted', 'Total dû converti')}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {formatAmount(
                              Number((payin.converted_amount || 0)) +
                                Number(((payin.exchange_rate ? payin.taxes / payin.exchange_rate : payin.taxes) || 0).toFixed(2)),
                              payin.currency
                            )}
                          </Typography>
                        </Grid>

                        <Grid item xs={12}>
                          <Typography variant="caption" sx={{ color: '#aaa', fontStyle: 'italic' }}>
                            {t('commissionPaymentSuccess.exchangeRateExplanation')} (1 {payin.currency} = {payin.exchange_rate} {payin.original_currency})
                          </Typography>
                        </Grid>
                      </>
                    ) : (
                      <>
                        <Grid item xs={12}>
                          <Divider sx={{ my: 0.5 }} />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" sx={{ color: '#888' }}>
                            {t('invoiceDetail.totalCommissionTaxesEuros', 'Montant total dû (commission + taxes) en EUR')}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {formatAmount(
                              Number((payin.commission_amount || 0)) + Number((payin.taxes || 0)),
                              'EUR'
                            )}
                          </Typography>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </Paper>
              ))
            )}

            {loadingPayins && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={28} />
              </Box>
            )}

            {hasMorePayins && !loadingPayins && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                <Button
                  variant="outlined"
                  onClick={() => loadPayins(payinsPage + 1, false)}
                  sx={{ borderRadius: 2 }}
                >
                  {t('commissionPaymentSuccess.loadMore', 'Charger plus')}
                </Button>
              </Box>
            )}

            {!hasMorePayins && payins.length > 0 && (
              <Typography variant="caption" sx={{ color: '#aaa', display: 'block', textAlign: 'center', mt: 2 }}>
                {t('commissionPaymentSuccess.noMoreData', 'Toutes les commandes sont affichées')}
              </Typography>
            )}
          </AccordionDetails>
        </Accordion>
      </Card>
    </Container>
  );
};

export default OwnerInvoiceDetailPage;
